import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ethers } from "ethers";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { useParams } from "react-router-dom";

import CheckIcon from "assets/icons/CheckIcon";
import CopyIcon from "assets/icons/CopyIcon";
import { ReferralCodeStats, useUserReferralCode } from "domain/referrals";
import { useChainId } from "lib/chains";
import { helperToast } from "lib/helperToast";
import useWallet from "lib/wallets/useWallet";

import Button from "components/Button/Button";
import { ButtonRowScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";
import Tabs from "components/Tabs/Tabs";

import { ReferralCodeForm } from "./JoinReferralCode";
import { getReferralCodeTradeUrl } from "./referralsHelper";

const ReferralCodeCard = ({
  currentRebatePercentage,
  affiliateReferralCodesStats,
  openGenerateCodeModal,
}: {
  currentRebatePercentage: string;
  affiliateReferralCodesStats: ReferralCodeStats[];
  openGenerateCodeModal: () => void;
}) => {
  useLingui();
  const { chainId } = useChainId();
  const { account: walletAccount, signer } = useWallet();
  const { account: queryAccount } = useParams<{ account?: string }>();
  let account: string | undefined;
  if (queryAccount && ethers.isAddress(queryAccount)) {
    account = ethers.getAddress(queryAccount);
  } else {
    account = walletAccount;
  }
  const [valueSelected, setValueSelected] = React.useState("referral-code");
  const { userReferralCodeString } = useUserReferralCode(signer, chainId, account);

  // const { account } = useWallet();
  // const { data: traderDetail } = useGetTraderDetail(account);

  const latestAffiliateData = useMemo(() => {
    if (affiliateReferralCodesStats?.length > 0) {
      return affiliateReferralCodesStats[affiliateReferralCodesStats.length - 1];
    }
    return undefined;
  }, [affiliateReferralCodesStats]);

  const referralTabOptions = [
    { value: "referral-code", label: t`Referral Code` },
    { value: "referral-link", label: t`Referral Link` },
    { value: "enter-code", label: t`Enter Code` },
  ];

  return (
    <div className="w-full rounded-10 bg-gray-darker p-12 lg:w-[350px] xl:w-[365px] xl:p-16">
      <div className="mb-18 flex items-start justify-between gap-8">
        <p className="leading-none max-w-[221px] text-[18px] font-medium text-white">
          <Trans>
            Invite a fren to earn up to a{" "}
            <span className="font-normal italic">{currentRebatePercentage || 5}% rebate</span>
          </Trans>
        </p>
        <button
          type="button"
          className="inline-flex size-32 items-center justify-center rounded-full border border-white-alpha-8 bg-white-alpha-2 text-white-alpha-60 hover:text-white-alpha-100"
          aria-label={t`Open referral details`}
        >
          <FiArrowUpRight className="size-14" />
        </button>
      </div>
      <div className="mt-12">
        <ButtonRowScrollFadeContainer>
          <Tabs
            options={referralTabOptions}
            onChange={setValueSelected}
            selectedValue={valueSelected}
            variant="filled"
            size="medium"
            className="w-full"
            regularOptionClassname="!px-12 !min-w-0"
          />
        </ButtonRowScrollFadeContainer>
      </div>
      <div className="mt-20 w-full">
        {/* {valueSelected === 'referral-code' && (traderDetail?.referral_code ? <ReferralCodeContent refCode={traderDetail?.referral_code} /> : <GenerateCodeContent open={openGenerateCodeModal} />)} */}
        {valueSelected === "referral-code" &&
          (latestAffiliateData?.referralCode ? (
            <ReferralCodeContent refCode={latestAffiliateData?.referralCode} />
          ) : (
            <GenerateCodeContent open={openGenerateCodeModal} />
          ))}
        {/* {valueSelected === 'referral-link' && (traderDetail?.referral_code ? <ReferralLinkContent refCode={traderDetail?.referral_code} /> : <GenerateCodeContent open={openGenerateCodeModal} />)} */}
        {valueSelected === "referral-link" &&
          (latestAffiliateData?.referralCode ? (
            <ReferralLinkContent refCode={latestAffiliateData?.referralCode} />
          ) : (
            <GenerateCodeContent open={openGenerateCodeModal} />
          ))}
        {valueSelected === "enter-code" && <ReferralCodeForm userReferralCodeString={userReferralCodeString} />}
      </div>
    </div>
  );
};

const ReferralCodeContent = ({ refCode }: { refCode?: string | undefined }) => {
  const [copied, setCopied] = useState(false);
  const idTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (copied) {
      idTimeout.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
    return () => {
      if (idTimeout.current) {
        clearTimeout(idTimeout.current);
        idTimeout.current = null;
      }
    };
  }, [copied]);

  return (
    <div className="flex flex-col gap-10">
      <p className="text-12 font-medium leading-[1.5] text-gray-lighter">
        <Trans>Share with your ferns to start earning from a share of the revenue</Trans>
      </p>
      <div className="flex items-center gap-12">
        <span className="!text-[18px] font-medium leading-[1] text-gray-lightest">{refCode}</span>
        {copied ? (
          <CheckIcon className="size-23 text-gray-lightest" />
        ) : (
          <CopyIcon
            className="size-23 cursor-pointer text-gray-lightest"
            onClick={async () => {
              setCopied(true);
              try {
                await navigator.clipboard.writeText(getReferralCodeTradeUrl(refCode || ""));
                helperToast.success(t`Referral link copied to clipboard!`);
              } catch (error) {
                setCopied(false);
                helperToast.error(t`Failed to copy referral link.`);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

const ReferralLinkContent = ({ refCode }: { refCode?: string | undefined }) => {
  const [copied, setCopied] = useState(false);
  const idTimeout = useRef<NodeJS.Timeout | null>(null);

  const url = useMemo(() => {
    return getReferralCodeTradeUrl(refCode || "");
  }, [refCode]);

  useEffect(() => {
    if (copied) {
      idTimeout.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
    return () => {
      if (idTimeout.current) {
        clearTimeout(idTimeout.current);
        idTimeout.current = null;
      }
    };
  }, [copied]);

  return (
    <div className="flex flex-col gap-10">
      <p className="text-12 font-medium leading-[1.5] text-gray-lighter">
        <Trans>Share with your ferns to start earning from a share of the revenue</Trans>
      </p>
      <div className="flex items-center gap-10">
        <p className="w-max !text-[12px] font-medium leading-[1] text-gray-lightest">{url}</p>
        {copied ? (
          <CheckIcon className="size-23 text-gray-lightest" />
        ) : (
          <CopyIcon
            className="size-23 cursor-pointer text-gray-lightest"
            onClick={async () => {
              setCopied(true);
              try {
                await navigator.clipboard.writeText(url || "");
                helperToast.success(t`Referral link copied to clipboard!`);
              } catch (error) {
                setCopied(false);
                helperToast.error(t`Failed to copy referral link.`);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

const GenerateCodeContent = ({ open }: { open: () => void }): JSX.Element => {
  return (
    <div className="flex flex-col gap-14">
      <p className="text-12 font-medium leading-[1.5] text-gray-lighter">
        <Trans>Create & customize referral code to start inviting frens</Trans>
      </p>
      <div className="flex flex-col gap-12">
        <Button
          variant="primary"
          size="medium"
          className="w-max !px-24 !py-8"
          onClick={() => {
            if (open) open?.();
          }}
        >
          <Trans>Create code</Trans>
        </Button>
      </div>
    </div>
  );
};

export default ReferralCodeCard;
